import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Divider,
  Chip,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { getStatusLabel, getStatusColor } from '../../utils/availability';
import { getProductUrl } from '../../config';

const CampDetails = ({ camps, locations }) => {
  const { handle } = useParams();
  const navigate = useNavigate();
  const [selectedLocation, setSelectedLocation] = useState(locations?.[0] || null);

  const camp = camps.find(c => c.handle === handle);

  if (!camp) {
    return (
      <Container sx={{ py: 4, textAlign: 'center' }}>
        <Typography variant="h4">Camp not found</Typography>
        <Button variant="contained" onClick={() => navigate('/')} sx={{ mt: 2 }}>
          Return to Camp Schedule
        </Button>
      </Container>
    );
  }

  // Filter weekSlots by selected location
  const filteredSlots = camp.weekSlots.filter(s =>
    selectedLocation ? s.location === selectedLocation : true
  );

  const availableSlots = filteredSlots.filter(s => s.status !== 'sold_out');
  const soldOutSlots = filteredSlots.filter(s => s.status === 'sold_out');

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Button variant="outlined" onClick={() => navigate('/')} sx={{ mb: 3 }}>
        ← Back to Schedule
      </Button>

      <Grid container spacing={4}>
        {/* Left column */}
        <Grid item xs={12} md={5}>
          <Box sx={{ position: 'sticky', top: 20 }}>
            {camp.imageUrl && (
              <Box
                component="img"
                src={camp.imageUrl}
                alt={camp.imageAlt}
                sx={{
                  width: '100%',
                  borderRadius: 2,
                  mb: 2,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                }}
              />
            )}
            <Card variant="outlined" sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Ages: 10-12 • {camp.tags.includes('beginner') ? 'Beg-Int' : 'Beg-Adv'}
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Price: ${parseFloat(camp.weekSlots[0]?.price || 0).toFixed(2)} USD
                </Typography>
              </CardContent>
            </Card>
          </Box>
        </Grid>

        {/* Right column */}
        <Grid item xs={12} md={7}>
          <Typography variant="h3" component="h1" gutterBottom>
            {camp.title}
          </Typography>
          <Typography variant="body1" paragraph sx={{ mb: 4 }}>
            {camp.description}
          </Typography>

          {/* Location filter */}
          {locations.length > 1 && (
            <Box sx={{ mb: 3 }}>
              <ToggleButtonGroup
                value={selectedLocation}
                exclusive
                onChange={(e, val) => { if (val !== null) setSelectedLocation(val); }}
                size="small"
              >
                {locations.map(loc => (
                  <ToggleButton key={loc} value={loc} sx={{ px: 3 }}>
                    {loc}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>
          )}

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ mb: 2 }}>Available Dates</Typography>

            {availableSlots.map((slot) => {
              const statusColor = getStatusColor(slot.status);
              const statusLabel = getStatusLabel(slot.status, slot.quantityAvailable);

              return (
                <Card
                  key={slot.variantId}
                  variant="outlined"
                  sx={{
                    mb: 2,
                    borderColor: slot.status === 'low' ? '#ff9800' : '#e0e0e0'
                  }}
                >
                  <CardContent sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box>
                        <Typography variant="h6">{slot.dates}</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5, gap: 1 }}>
                          <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: statusColor }} />
                          <Chip
                            label={statusLabel}
                            size="small"
                            sx={{
                              backgroundColor: statusColor,
                              color: 'white',
                              fontWeight: 'medium',
                              fontSize: '0.7rem',
                              height: 22
                            }}
                          />
                        </Box>
                      </Box>
                      <Button
                        variant="contained"
                        color="primary"
                        sx={{ borderRadius: '20px', px: 3 }}
                        onClick={() => window.open(getProductUrl(camp.handle), '_blank')}
                      >
                        Select Date
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              );
            })}

            {/* Sold out dates */}
            {soldOutSlots.length > 0 && (
              <>
                <Typography variant="h6" color="text.secondary" sx={{ mt: 3, mb: 1 }}>
                  Sold Out
                </Typography>
                {soldOutSlots.map((slot) => (
                  <Card
                    key={slot.variantId}
                    variant="outlined"
                    sx={{ mb: 1.5, borderColor: 'rgba(244,67,54,0.3)', opacity: 0.6 }}
                  >
                    <CardContent sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography variant="h6" sx={{ textDecoration: 'line-through' }}>
                            {slot.dates}
                          </Typography>
                          <Chip
                            label="Sold Out"
                            size="small"
                            sx={{
                              backgroundColor: '#f44336',
                              color: 'white',
                              fontWeight: 'medium',
                              fontSize: '0.7rem',
                              height: 22,
                              mt: 0.5
                            }}
                          />
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </>
            )}

            {filteredSlots.length === 0 && (
              <Box sx={{ p: 3, textAlign: 'center', bgcolor: '#f5f5f5', borderRadius: 2 }}>
                <Typography variant="h6" color="text.secondary">
                  No dates currently available for this location
                </Typography>
              </Box>
            )}
          </Box>

          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Button
              variant="outlined"
              size="large"
              onClick={() => window.open(getProductUrl(camp.handle), '_blank')}
              sx={{ borderRadius: '20px', px: 4, py: 1 }}
            >
              View on Store
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Container>
  );
};

export default CampDetails;
